import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, X } from 'lucide-react';
import api from '../services/api';
import JobForm from './JobForm';
import '../styles/Joblist.css';

const JobList = () => {
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });
    const [showForm, setShowForm] = useState(false);
    const [selectedJobId, setSelectedJobId] = useState(null);

    useEffect(() => {
        loadJobs();
    }, [pagination.page]);

    const loadJobs = async () => {
        try {
            setIsLoading(true);
            console.log('📡 Loading jobs...');
            const response = await api.get(
                `/JobGeneration.php?page=${pagination.page}&limit=${pagination.limit}`
            );
            console.log('📦 Jobs response:', response.data);
            if (response.data.success) {
                setJobs(response.data.data.jobs);
                setPagination(response.data.data.pagination);
            } else {
                throw new Error(response.data.message || 'Failed to load jobs');
            }
        } catch (err) {
            console.error('❌ Error loading jobs:', err);
            alert('Failed to load jobs: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchTerm.trim()) {
            loadJobs();
            return;
        }
        try {
            setIsLoading(true);
            const response = await api.get(
                `/JobGeneration.php?action=search&search=${encodeURIComponent(searchTerm)}`
            );
            if (response.data.success) {
                setJobs(response.data.data.jobs);
            } else {
                throw new Error(response.data.message || 'Search failed');
            }
        } catch (err) {
            console.error('❌ Search error:', err);
            alert('Search failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsLoading(false);
        }
    };

    const handleNewJob = () => {
        setSelectedJobId(null);
        setShowForm(true);
    };

    const handleEditJob = (jobId) => {
        setSelectedJobId(jobId);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setSelectedJobId(null);
    };

    const handleSaved = () => {
        setShowForm(false);
        setSelectedJobId(null);
        loadJobs();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-IN');
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    if (showForm) {
        return (
            <JobForm
                jobId={selectedJobId}
                onClose={handleCloseForm}
                onSaved={handleSaved}
            />
        );
    }

    return (
        <div className="job-list-container">
            <div className="job-list-header">
                <h1>Job Management</h1>
                <button className="btn btn-primary" onClick={handleNewJob}>
                    <Plus size={20} />
                    New Job
                </button>
            </div>

            <div className="job-list-toolbar">
                <form onSubmit={handleSearch} className="search-form">
                    <div className="search-input-wrapper">
                        <Search size={20} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by job code, MBL, HBL, customer..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="btn-clear"
                                onClick={() => { setSearchTerm(''); loadJobs(); }}
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    <button type="submit" className="btn btn-secondary">
                        Search
                    </button>
                </form>
            </div>

            {isLoading ? (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading jobs...</p>
                </div>
            ) : (
                <>
                    <div className="job-table-wrapper">
                        <table className="job-table">
                            <thead>
                                <tr>
                                    <th>Job Code</th>
                                    <th>Date</th>
                                    <th>Job Type</th>
                                    <th>Customer</th>
                                    <th>Load Port</th>
                                    <th>Destination</th>
                                    <th>Invoice No</th>
                                    <th>Invoice Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {jobs.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="empty-state">
                                            No jobs found. Click "New Job" to create one.
                                        </td>
                                    </tr>
                                ) : (
                                    jobs.map(job => {
                                        const isSubJob = job.StatusSub === 1 || job.MainJobID > 0;
                                        return (
                                            <tr key={job.JobID} className={isSubJob ? 'subjob-row' : ''}>
                                                <td>
                                                    {isSubJob && (
                                                        <span className="subjob-icon" title="Sub-Job">↳ </span>
                                                    )}
                                                    <span className="job-code">{job.JobCode}</span>
                                                    {isSubJob && job.MainJobCode && (
                                                        <div className="main-job-ref">
                                                            <small>Main: {job.MainJobCode}</small>
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{formatDate(job.JobDate)}</td>
                                                <td>{job.JobDescription}</td>
                                                <td>{job.InvoiceToName}</td>
                                                <td>{job.LoadPort}</td>
                                                <td>{job.DestinationPort}</td>
                                                <td>
                                                    {job.InvoiceNo && (
                                                        <span className="invoice-badge">
                                                            {job.InvoiceNo}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{formatDate(job.InvoiceDate)}</td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => handleEditJob(job.JobID)}
                                                            title="Edit Job"
                                                        >
                                                            <Edit size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pagination.pages > 1 && (
                        <div className="pagination">
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                            >
                                Previous
                            </button>
                            <span className="pagination-info">
                                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                            </span>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.pages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default JobList;